import {Fragment, useEffect, useRef, useState} from "react";
import "./chat-bot.styles.scss";
import {getBotReply, WELCOME_MESSAGE} from "../../utils/chatbot/chatbot.utils";
import {DEFAULT_SUGGESTIONS} from "../../utils/chatbot/knowledge-base.constants";

const LINK_PATTERN = /(https?:\/\/[^\s)]+|[\w.+-]+@[\w-]+\.[\w.]+[a-z])/gi;

// Render plain-text answers with clickable URLs and email addresses.
const MessageText = ({text}) =>
  text.split(LINK_PATTERN).map((part, index) => {
    if (index % 2 === 0) return <Fragment key={index}>{part}</Fragment>;
    const href = part.includes("@") && !part.startsWith("http") ? `mailto:${part}` : part;
    return (
      <a key={index} href={href} target="_blank" rel="noreferrer">
        {part}
      </a>
    );
  });

const createWelcome = () => [{id: 0, from: "bot", text: WELCOME_MESSAGE}];

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(createWelcome);
  const [suggestions, setSuggestions] = useState(DEFAULT_SUGGESTIONS);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  const contextRef = useRef({});
  const nextIdRef = useRef(1);
  const timerRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, isTyping, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    inputRef.current?.focus();

    const onKeyDown = (event) => event.key === "Escape" && setIsOpen(false);
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen]);

  const addMessage = (from, text) =>
    setMessages((prev) => [...prev, {id: nextIdRef.current++, from, text}]);

  const ask = (text) => {
    const question = text.trim();
    if (!question || isTyping) return;

    addMessage("user", question);
    setInput("");
    setSuggestions([]);
    setIsTyping(true);

    const reply = getBotReply(question, contextRef.current);
    contextRef.current = reply.context;

    // A short "typing" pause makes instant answers feel less abrupt.
    const delay = Math.min(1100, 350 + reply.text.length * 2);
    timerRef.current = setTimeout(() => {
      addMessage("bot", reply.text);
      setSuggestions(reply.suggestions);
      setIsTyping(false);
    }, delay);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    ask(input);
  };

  const resetChat = () => {
    clearTimeout(timerRef.current);
    contextRef.current = {};
    setMessages(createWelcome());
    setSuggestions(DEFAULT_SUGGESTIONS);
    setIsTyping(false);
    setInput("");
    inputRef.current?.focus();
  };

  return (
    <div className="chat-bot">
      {isOpen && (
        <div className="chat-panel" role="dialog" aria-label="Ask about Suraj">
          <div className="chat-header">
            <div className="chat-avatar" aria-hidden="true">SV</div>
            <div className="chat-title">
              <h4>Ask about Suraj</h4>
              <p>
                <span className="chat-status-dot"/> Usually replies instantly
              </p>
            </div>
            <button className="chat-icon-button" onClick={resetChat} aria-label="Restart conversation" title="Restart">
              <i className="fa fa-refresh"></i>
            </button>
            <button className="chat-icon-button" onClick={() => setIsOpen(false)} aria-label="Close chat" title="Close">
              <i className="fa fa-times"></i>
            </button>
          </div>

          <div className="chat-messages" ref={listRef} aria-live="polite">
            {messages.map((message) => (
              <div key={message.id} className={`chat-message ${message.from}`}>
                <MessageText text={message.text}/>
              </div>
            ))}

            {isTyping && (
              <div className="chat-message bot chat-typing" aria-label="Typing">
                <span/>
                <span/>
                <span/>
              </div>
            )}

            {!isTyping && suggestions.length > 0 && (
              <div className="chat-suggestions">
                {suggestions.map((suggestion) => (
                  <button key={suggestion} className="chat-suggestion" onClick={() => ask(suggestion)}>
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>

          <form className="chat-input" onSubmit={handleSubmit}>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="Ask about his career, skills, schooling…"
              aria-label="Your question"
              maxLength={200}
            />
            <button type="submit" disabled={!input.trim() || isTyping} aria-label="Send">
              <i className="fa fa-paper-plane"></i>
            </button>
          </form>
        </div>
      )}

      <button
        className={`chat-launcher ${isOpen ? "open" : ""}`}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label={isOpen ? "Close chat" : "Ask about Suraj"}
        aria-expanded={isOpen}
      >
        <i className={`fa ${isOpen ? "fa-times" : "fa-comments"}`}></i>
        {!isOpen && <span className="chat-launcher-label">Ask about Suraj</span>}
      </button>
    </div>
  );
};

export default ChatBot;
